import React, { useState, useEffect, useMemo, useCallback } from "react";
import TemplateDefinitionView from "../views/TemplateDefinitionView";
import { executeWorkflowHandshake } from "../../Workflow-esg/Workflow_XLS_Support";
import ESGTemplateDTO from "../domains/ESGTemplateDTO";
import { TemplateMetadataController } from "./TemplateMetadataController";

// 🎯 Prop 'onInfraReady' added to bridge Step 3 and Step 4
export default function TemplateDefinitionController({ selectedFile, onInfraReady }) {
  const userId = sessionStorage.getItem("SEC_USER_GUID") || "SYSTEM_USER";

  const [templateData, setTemplateData] = useState(new ESGTemplateDTO());
  const [isProcessing, setIsProcessing] = useState(false);
  const [infraStatus, setInfraStatus] = useState(null);
  const [isSavingMeta, setIsSavingMeta] = useState(false);

  const metaController = useMemo(() => 
    new TemplateMetadataController(userId), [userId]
  );

  const defaultValidFrom = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); 

    let startDate = new Date(currentYear, 0, 1);

    if (currentMonth >= 3) {
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      startDate = new Date(currentYear, quarterStartMonth, 1);
    }

    return startDate.toISOString().split('T')[0]; 
  }, []);

  const checkExistingInfrastructure = useCallback(async (folderName) => {
    console.group(`🌐 [INFRA_CHECK]: ${folderName}`);
    setIsProcessing(true);
    
    const checkPayload = {
      action: "LIST_TEMPLATE_FOLDERS",
      user_id: userId,
      payload: {
        bucketName: process.env.REACT_APP_SITE_TEMPLATE_BUCKET || "esg-template-management"
      }
    };

    try {
      const response = await executeWorkflowHandshake(
        "doorman/InfraExists",
        checkPayload,
        "InfraExists"
      );

      const existingItems = response?.data || [];
      
      if (existingItems.includes(folderName)) {
        console.log("📂 [MATCH_FOUND]: Directory exists in esg-template-management.");
        
        const mirroredData = await metaController.loadMetadata(folderName);
        
        if (mirroredData) {
          setTemplateData(new ESGTemplateDTO({
            name: folderName,
            description: mirroredData.current_state?.description || "",
            validFrom: mirroredData.current_state?.valid_from || defaultValidFrom,
            status: mirroredData.current_state?.status || "PROVISIONED"
          }));
        }

        const statusUpdate = {
          status: "PROVISIONED",
          root_path: `${folderName}/`,
          message: mirroredData ? "Metadata loaded." : "Existing structure detected."
        };

        setInfraStatus(statusUpdate);

        // 🎯 Notify Orchestrator to enable Step 4
        if (onInfraReady) {
          onInfraReady(statusUpdate);
        }

      } else {
        console.log("🆕 [NO_MATCH]: Clean start for new infrastructure.");
        setInfraStatus(null); 
      }
    } catch (err) {
      console.error("🚨 [DISCOVERY_ERROR]:", err.message);
    } finally {
      console.groupEnd();
      setIsProcessing(false);
    }
  }, [userId, metaController, defaultValidFrom, onInfraReady]);

  useEffect(() => {
    if (selectedFile) {
      const fileName = selectedFile?.filename || selectedFile?.fileName || selectedFile?.key || "";
      const folderMatch = fileName.split('.')[0];

      setTemplateData(new ESGTemplateDTO({
        name: folderMatch,
        description: "",
        isActive: true,
        status: "DRAFT" 
      }));

      checkExistingInfrastructure(folderMatch);
    }
  }, [selectedFile, checkExistingInfrastructure]);

  const handleSaveMetadata = async () => {
    setIsSavingMeta(true);
    try {
      const result = await metaController.saveStructureDefinition(templateData, selectedFile);
      if (result.success) {
        console.log("🏆 [METADATA_SYNCED]: Structure Definition versioned on S3.");
      }
    } catch (err) {
      console.error("🚨 [SAVE_META_ERROR]:", err.message);
    } finally {
      setIsSavingMeta(false);
    }
  };

  const handleCreateTemplate = async () => {
    setIsProcessing(true);

    const createPayload = {
      action: "SETUP_TEMPLATE_INFRA",
      user_id: userId,
      payload: {
        bucketName: process.env.REACT_APP_SITE_CLEAN_BUCKET || "esg-staging-clean",
        target_bucket: process.env.REACT_APP_SITE_TEMPLATE_BUCKET || "esg-template-management",
        fileName: selectedFile?.filename || selectedFile?.fileName || selectedFile?.key,
        site_key: process.env.REACT_APP_SITE_KEY || "SUSTINANRGPIXCOM",
        origin: window.location.origin
      }
    };

    try {
      const response = await executeWorkflowHandshake(
        "doorman/TemplateInfra",
        createPayload,
        "TemplateInfra"
      );

      if (response?.success && response?.data) {
        setInfraStatus(response.data);
        
        // 🎯 Notify Orchestrator to enable Step 4 for new infra
        if (onInfraReady) {
          onInfraReady(response.data);
        }
        
        await handleSaveMetadata(); 
      }
    } catch (err) {
      console.error("🚨 [PROVISION_ERROR]:", err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const isReadyToCreate =
    selectedFile &&
    templateData.description.trim() !== "" &&
    userId !== "UNKNOWN_USER";

  return (
    <TemplateDefinitionView
      templateData={templateData}
      setTemplateData={setTemplateData}
      defaultDate={defaultValidFrom}
      isReady={isReadyToCreate}
      isProcessing={isProcessing || isSavingMeta}
      onCreate={handleCreateTemplate}
      onSaveMeta={handleSaveMetadata}
      infraStatus={infraStatus}
    />
  );
}